import { useQueries } from "@tanstack/react-query";
import { useDebounce } from "./useDebounce";

export type SimilarReason = "name" | "email" | "phone" | "code";

export interface SimilarMatch<T> {
  item: T;
  reasons: SimilarReason[];
}

interface UseSimilarRecordsOptions<T> {
  /** Distinguishes the cache of each entity and scope (e.g. zone). */
  key: string;
  /** Server-side search terms; each one is fetched separately. */
  terms: string[];
  fetchCandidates: (term: string) => Promise<T[]>;
  getId: (item: T) => string;
  /** Why the candidate looks like a duplicate, or an empty list if it doesn't. */
  match: (item: T) => SimilarReason[];
  enabled?: boolean;
}

const REASON_WEIGHT: Record<SimilarReason, number> = {
  code: 3,
  email: 3,
  phone: 2,
  name: 1,
};

export const useSimilarRecords = <T>({
  key,
  terms,
  fetchCandidates,
  getId,
  match,
  enabled = true,
}: UseSimilarRecordsOptions<T>) => {
  const debouncedTerms = useDebounce(terms.join("|"), 400)
    .split("|")
    .filter(Boolean);

  const results = useQueries({
    queries: (enabled ? debouncedTerms : []).map((term) => ({
      queryKey: ["SIMILAR_RECORDS", key, term],
      queryFn: () => fetchCandidates(term),
      staleTime: 30_000,
    })),
  });

  const unique = new Map<string, T>();
  results.forEach((result) =>
    result.data?.forEach((item) => unique.set(getId(item), item)),
  );

  const matches: SimilarMatch<T>[] = [...unique.values()]
    .map((item) => ({ item, reasons: match(item) }))
    .filter((result) => result.reasons.length > 0)
    .sort(
      (a, b) =>
        Math.max(...b.reasons.map((r) => REASON_WEIGHT[r])) -
        Math.max(...a.reasons.map((r) => REASON_WEIGHT[r])),
    );

  return {
    matches: enabled ? matches : [],
    isSearching: results.some((result) => result.isFetching),
  };
};
